import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/**
 * Crash reporting (blocker B3, web half). The DSN is baked at build time via
 * VITE_SENTRY_DSN (set in the committed .do/*.yaml specs — a Sentry browser
 * DSN is a public client key by design). Blank → Sentry stays off, so local
 * dev and tests never talk to the network. VITE_APP_VERSION rides along from
 * the same specs and names the build each event came from.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// `${APP_VERSION}` substitutes to '' when the deploy workflow doesn't set it
// (e.g. a spec applied by hand), and an empty release tags every event with a
// blank version — worse than none. Only a non-empty string is a release.
const release = (import.meta.env.VITE_APP_VERSION as string | undefined) || undefined;

// `vite build` reports MODE=production on every build, staging included — the
// deploy's VITE_APP_ENV is the only thing that tells the two apart. MODE is the
// local-dev fallback ('development' under `vite dev`).
const environment = (import.meta.env.VITE_APP_ENV as string | undefined) || import.meta.env.MODE;

/** Locked by #228: full staging traces, representative production sample. */
export function traceSampleRate(appEnvironment: string): number {
  if (appEnvironment === 'staging') return 1;
  if (appEnvironment === 'production') return 0.2;
  return 0;
}

/** Browser tracing driven by React Router's matched route definitions. */
export function browserTracingIntegration() {
  return Sentry.reactRouterV7BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
  });
}

export function initSentry(): void {
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment,
    release,
    // #228 observability contract: 20% production, 100% staging, none locally.
    tracesSampleRate: traceSampleRate(environment),
    integrations(defaultIntegrations) {
      return [...defaultIntegrations, browserTracingIntegration()];
    },
    // Route instrumentation uses the matched React Router templates declared in
    // App.tsx (`/trips/:tripId`), not concrete URLs or regex guesses.
  });
}

export const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export { Sentry };
